import { useState } from 'react';
import { RelatedApps, RELATED_BY_APP } from '@hypneuronerd/ui';
import type { Card, SortState } from '../data/types';
import { SORT_COLUMNS } from '../data/types';
import {
  cardsInColumn,
  copyToClipboard,
  downloadFile,
  sortToMarkdown,
  sortToText,
} from '../lib/export';
import styles from './Portrait.module.css';

interface PortraitProps {
  sort: SortState;
  deck: Card[];
  title: string;
  onTitleChange: (title: string) => void;
  onEdit: () => void;
  onReset: () => void;
}

export function Portrait({ sort, deck, title, onTitleChange, onEdit, onReset }: PortraitProps) {
  const [feedback, setFeedback] = useState<string | null>(null);

  function ping(msg: string) {
    setFeedback(msg);
    window.setTimeout(() => setFeedback(null), 1800);
  }

  async function handleCopy() {
    const ok = await copyToClipboard(sortToText(sort, title, deck));
    ping(ok ? 'copied to clipboard' : 'copy failed');
  }

  function handleMarkdown() {
    const safe = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    downloadFile(`${safe || 'card-sort'}.md`, sortToMarkdown(sort, title, deck), 'text/markdown');
    ping('downloaded markdown');
  }

  function handlePrint() {
    window.print();
  }

  return (
    <>
      <article className={styles.page}>
        <header className={styles.header}>
          <span className={styles.ornament} aria-hidden="true">
            ✦
          </span>
          <input
            className={styles.titleInput}
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            maxLength={80}
            aria-label="portrait title"
          />
          <p className={styles.composed}>
            composed{' '}
            {new Date().toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </header>

        {SORT_COLUMNS.map((col) => {
          const items = cardsInColumn(sort, col.id, deck);
          return (
            <section key={col.id} className={styles.section}>
              <h2 className={styles.sectionTitle} style={{ color: col.accent }}>
                {col.label}
              </h2>
              {items.length === 0 ? (
                <p className={styles.empty}>(none)</p>
              ) : (
                <ul className={styles.list}>
                  {items.map((c) => (
                    <li key={c.id} className={styles.item}>
                      <span className={styles.itemName}>{c.name}</span>
                      <span className={styles.itemDesc}>{c.description}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
      </article>

      <div className={styles.actions}>
        <button type="button" className={styles.action} onClick={handlePrint}>
          print
        </button>
        <button type="button" className={styles.action} onClick={handleCopy}>
          copy as text
        </button>
        <button type="button" className={styles.action} onClick={handleMarkdown}>
          download .md
        </button>
        <button type="button" className={styles.action} onClick={onEdit}>
          edit
        </button>
        <button type="button" className={styles.danger} onClick={onReset}>
          start over
        </button>
      </div>

      {feedback && (
        <div className={styles.feedback} role="status" aria-live="polite">
          {feedback}
        </div>
      )}

      <RelatedApps apps={RELATED_BY_APP['card-sort']} surface="cream" />
    </>
  );
}
